import { z } from 'zod';

/**
 * Voice dictation: hotkey opens the microphone, speech is transcribed on device, and the
 * text is pasted into whatever textbox still holds the caret. Mirrors the Swift types
 * under `ContextKit/Sources/observer/lib/Dictation/`; string values must stay identical.
 */

/** Hummingbird's lowercase spelling, which `globalShortcut.register` accepts as-is. */
export const DEFAULT_DICTATION_SHORTCUT = 'command+shift+d';

export const DictationWindowDimensions = {
  Width: 380,
  /** Starting height. The window grows upward with the transcript, up to MaxHeight. */
  Height: 150,
  /** Roughly six transcript lines; past this the transcript scrolls its own tail. */
  MaxHeight: 300,
  /** Gap between the HUD's bottom edge and the bottom of the work area. */
  BottomMarginPx: 64,
  /** Padding the renderer leaves around the panel for its shadow and glow. */
  ChromePx: 48,
} as const;

export const DictationSessionStates = {
  Idle: 'idle',
  Preparing: 'preparing',
  Listening: 'listening',
  Stopping: 'stopping',
  Cleaning: 'cleaning',
} as const;

export type DictationSessionState =
  (typeof DictationSessionStates)[keyof typeof DictationSessionStates];

export const DictationEngines = {
  Local: 'local',
  Remote: 'remote',
} as const;

/** Mirrors Swift `DictationStartFailure`. Every case is something the HUD can explain. */
export const DictationStartFailures = {
  MeetingActive: 'meeting_active',
  /** Weights are still loading; retrying shortly should work. */
  ModelPreparing: 'model_preparing',
  /** Weights are absent and no preparation is running. */
  ModelNotReady: 'model_not_ready',
  RemoteEngineUnavailable: 'remote_engine_unavailable',
  AudioStartFailed: 'audio_start_failed',
  MicrophonePermission: 'microphone_permission',
  AlreadyRunning: 'already_running',
  /** Below `DICTATION_MIN_MACOS_VERSION`, where on-device transcription does not exist. */
  UnsupportedOs: 'unsupported_os',
} as const;

/** Same floor as Meeting Notes: both need the on-device transcription stack. */
export const DICTATION_MIN_MACOS_VERSION = { major: 14, minor: 2 } as const;

export type DictationStartFailure =
  (typeof DictationStartFailures)[keyof typeof DictationStartFailures];

const DICTATION_START_FAILURES: readonly string[] = Object.values(
  DictationStartFailures
);

export function isDictationStartFailure(
  value: string
): value is DictationStartFailure {
  return DICTATION_START_FAILURES.includes(value);
}

export const StartDictationPayloadSchema = z.object({
  sessionId: z.string().uuid(),
  inputDeviceId: z.string().optional(),
});

/** Mirrors Swift `DictationEndReason`. Only `committed` put text on screen. */
export const DictationEndReasons = {
  Committed: 'committed',
  Cancelled: 'cancelled',
  Timeout: 'timeout',
  DictationDisabled: 'dictation_disabled',
  EndedByMeeting: 'ended_by_meeting',
} as const;

export type DictationEndReason =
  (typeof DictationEndReasons)[keyof typeof DictationEndReasons];

export const StopDictationPayloadSchema = z.object({
  /** Absent stops whichever session is active. */
  sessionId: z.string().uuid().optional(),
  reason: z.nativeEnum(DictationEndReasons),
});

export const DictationTranscriptPayloadSchema = z.object({
  sessionId: z.string(),
  text: z.string(),
  isFinal: z.boolean(),
  chunkId: z.number(),
  utteranceId: z.string().optional(),
});

export type DictationTranscriptPayload = z.infer<
  typeof DictationTranscriptPayloadSchema
>;

/** Level-meter energy. Decorative, so a dropped sample costs nothing. */
export const DictationAudioLevelPayloadSchema = z.object({
  sessionId: z.string(),
  levels: z.array(z.number()),
});

export type DictationAudioLevelPayload = z.infer<
  typeof DictationAudioLevelPayloadSchema
>;

export const DictationSessionStatePayloadSchema = z.object({
  sessionId: z.string(),
  state: z.nativeEnum(DictationSessionStates),
  engine: z.nativeEnum(DictationEngines).optional(),
  failureReason: z.nativeEnum(DictationStartFailures).optional(),
  endReason: z.nativeEnum(DictationEndReasons).optional(),
});

export type DictationSessionStatePayload = z.infer<
  typeof DictationSessionStatePayloadSchema
>;
